import {
  BulkDatesMode,
  BulkUpdateDetails,
  Duration,
  SelectModeData
} from "PFApp/booking/booking_edit_mode_store";
import { UNDETERMINED_FUTURE } from "PFApp/constants/dates";
import { usePastDaysLimit } from "PFApp/hooks";
import { utc } from "PFCore/helpers/date";
import { useBookingsBulkUpdate } from "PFCore/hooks/queries/bookings/bulk_operation/use_bookings_bulk_update";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import {
  BookingForUpdate,
  BulkOperationBasePayload,
  BulkOperationResponse
} from "PFCore/services/bookings_bulk_operation";
import { RequirementType } from "PFTypes";

import { useFilterBookingsByDuration } from "../hooks/use_filter_bookings_by_duration";

type BulkUpdateDateParams = {
  selectedDate: string;
  mode: BulkDatesMode;
  selection: SelectModeData;
};

type BulkUpdateDetailsParams = {
  details?: BulkUpdateDetails;
  profileId?: number;
  selection: BulkOperationBasePayload;
};

type BulkUpdateDurationParams = {
  duration: Duration;
  filteredBookings: BookingForUpdate[];
  selection: SelectModeData;
};

type UseBulkUpdate = {
  onSuccess: (data: BulkOperationResponse) => void;
  onError?: VoidFunction;
};

type UseBulkUpdateReturn = {
  bulkUpdateDate: (params: BulkUpdateDateParams) => Promise<void>;
  bulkUpdateDetails: (params: BulkUpdateDetailsParams) => Promise<void>;
  bulkUpdateDuration: (params: BulkUpdateDurationParams) => void;
};

export const useBulkUpdate = ({ onSuccess, onError }: UseBulkUpdate): UseBulkUpdateReturn => {
  const { mutateAsync } = useBookingsBulkUpdate();
  const { formatISODate } = useDateFormatter();
  const pastDaysLimit = usePastDaysLimit();
  const { filterBookingsByDuration } = useFilterBookingsByDuration();

  const bulkUpdateDate = async ({ selectedDate, mode, selection }: BulkUpdateDateParams) => {
    const payload = {
      filters: selection.filters,
      exclude: selection.exclude,
      updates: {
        startDate: mode === BulkDatesMode.BulkUpdateStartDate ? selectedDate : undefined,
        endDate: mode === BulkDatesMode.BulkUpdateEndDate ? selectedDate : undefined
      },
      dateRange: {
        min:
          mode === BulkDatesMode.BulkUpdateStartDate
            ? selectedDate
            : formatISODate(utc().subtract(pastDaysLimit, "days")),
        max: mode === BulkDatesMode.BulkUpdateEndDate ? selectedDate : formatISODate(UNDETERMINED_FUTURE)
      }
    };
    try {
      const response = await mutateAsync(payload);
      onSuccess?.(response);
    } catch {
      onError?.();
    }
  };

  const bulkUpdateDetails = async ({ details, profileId, selection }: BulkUpdateDetailsParams) => {
    const payload = {
      filters: selection.filters,
      exclude: selection.exclude,
      updates: {
        bookingCategoryId: details?.bookingCategoryId.enabled
          ? details?.bookingCategoryId.value?.category?.id
          : undefined,
        title: details?.title.enabled ? details.title.value : undefined,
        description: details?.description.enabled ? details.description.value : undefined,
        profileId
      },
      dateRange: selection.dateRange ?? {
        min: formatISODate(utc().subtract(pastDaysLimit, "days")),
        max: formatISODate(UNDETERMINED_FUTURE)
      }
    };
    try {
      const response = await mutateAsync(payload);
      onSuccess?.(response);
    } catch {
      onError?.();
    }
  };

  const bulkUpdateDuration = async ({ duration, selection, filteredBookings }: BulkUpdateDurationParams) => {
    const getDurationInPercentageOrMinutes = () => {
      if (!duration.durationValue || duration.durationType === RequirementType.Load) {
        return duration.durationValue ?? undefined;
      }

      return duration.durationValue * 60;
    };
    const bookingsIds = filterBookingsByDuration(filteredBookings, duration).map(({ id }) => id);

    const payload = {
      filters: [
        {
          id: bookingsIds
        }
      ],
      exclude: selection.exclude,
      updates: {
        requirementType: duration.durationType ?? undefined,
        requirementValue: getDurationInPercentageOrMinutes()
      },
      dateRange: {
        min: formatISODate(utc().subtract(pastDaysLimit, "days")),
        max: formatISODate(UNDETERMINED_FUTURE)
      }
    };
    try {
      const response = await mutateAsync(payload);
      onSuccess?.(response);
    } catch {
      onError?.();
    }
  };

  return {
    bulkUpdateDate,
    bulkUpdateDetails,
    bulkUpdateDuration
  };
};
